import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Grid, Box, Typography, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../../../theme/colors";
import { Icon } from "../../../../../components/base";
import { ShaDowBox } from "../../../../../components/boxes";
import OwnerImg from "../../../../../assets/images/icons/owner.svg";
import { ByArea, NoDataCard } from "../../../../../components/parts";
import TenantImg from "../../../../../assets/images/icons/tenant.svg";
import { HorizontalTabs } from "../../../../../components/horizontalTabs";
import roomIcon from "../../../../../assets/images/icons/workspace/room.svg";
import PMCImg from "../../../../../assets/images/icons/sub-meter-and-public.svg";
import { title, iconSrc } from "../../../../../components/parts/connectionStatus";
import percentIcon from "../../../../../assets/images/icons/workspace/percent.svg";
import { currencyFormatter, formatDatetime, getData } from "../../../../../services";
import onePersonIcon from "../../../../../assets/images/icons/workspace/one-person.svg";
import twoPersonIcon from "../../../../../assets/images/icons/workspace/two-person.svg";
import { HorizontalArrowTabs } from "../../../../../components/horizontalArrowTabs";

const useStyles = makeStyles((theme) => ({
  header: {
    marginTop: theme.spacing(1.75),
  },
  headerText: {
    color: COLORS.darks[0][50],
    fontSize: 12,
    textAlign: "center",
  },
  row: {
    padding: `${theme.spacing(3)}px 0`,
    "&:not(:last-child)": {
      borderBottom: `1px solid ${COLORS.darks[0][10]}`,
    },
  },
  rowItem: {
    minHeight: 96,
    "&:not(:last-child)": {
      borderRight: `1px solid ${COLORS.darks[0][10]}`,
    },
  },
  icon: {
    textAlign: "center",
    marginBottom: theme.spacing(1),
    maxHeight: 50,
  },
  meterTitle: {
    fontSize: 18,
    fontWeight: 700,
  },
  boldText: {
    fontWeight: 700,
    textAlign: "center",
  },
  percent: {
    fontSize: 32,
    fontWeight: 300,
    lineHeight: "38.4px",
    color: COLORS.davisGrey,
  },
  line: {
    width: 40,
    height: 1,
    background: COLORS.davisGrey,
    margin: "3px auto",
  },
  naText: {
    color: COLORS.darks[0][25],
    fontSize: 21,
    fontWeight: "bold",
  },
  scroll: {
    "overflow-y": "scroll",
    height: 384,
  },
  noScroll: {
    height: "100%",
  },
  noScrollHeight: {
    height: "100%",
  },
}));

const pricingType = {
  FIX_PRICE: {
    icon: onePersonIcon,
    width: 33,
    height: 50,
  },
  PERCENT_SPACE: {
    icon: percentIcon,
    width: 38,
    height: 37,
  },
  BY_AREA: {
    component: (
      <ByArea rootStyle={{ paddingTop: 0, cursor: "initial" }} lineStyle={{ margin: 2 }} />
    ),
  },
  NUMBER_TENANTS: {
    icon: twoPersonIcon,
    width: 24,
    height: 50,
  },
  NUMBER_ROOMS: {
    icon: roomIcon,
    width: 24,
    height: 50,
  },
};

const owner = {
  BUILDING_OWNER: {
    iconSrc: OwnerImg,
    width: 44,
    height: 50,
    text: "Building owner",
  },
  TENANT: {
    iconSrc: TenantImg,
    width: 22,
    height: 50,
    text: "Tenant",
  },
  PMC: {
    iconSrc: PMCImg,
    width: "auto",
    height: 50,
    text: "PMC owner",
  },
};

const NA = () => {
  const classes = useStyles();

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ height: "100%" }}>
      <Typography className={classes.naText}>N/A</Typography>
    </Grid>
  );
};

export const Meters = ({ tId }) => {
  const classes = useStyles();
  const boxRef = useRef();
  const { id } = useParams();
  const [tab, setTab] = useState(0);
  const [unitNames, setUnitNames] = useState([]);
  const [results, setResults] = useState([]);
  const [meters, setMeters] = useState([]);
  const [activeAccountNumbers, setActiveAccountNumbers] = useState([]);
  const TabsComponent = unitNames.length > 1 ? HorizontalArrowTabs : HorizontalTabs;
  const [blockWidth, setBlockWidth] = useState();
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  useEffect(() => {
    getData(`/praas/v2/buildings/${id}/tenants/${tId}/electricity-analytics/`, (data) => {
      const names = [];
      const smartMeters = data.map((unit) => {
        names.push(`Unit ${unit.unit_id} Meters (${unit.smart_meters.length})`);
        return unit.smart_meters.map((smartMeter) => ({
          mpan: smartMeter.mpan,
          ownership: unit.owner,
          status: smartMeter.status,
          since_connected:
            smartMeter.status === "CONNECTED" && smartMeter.since_connected
              ? formatDatetime(new Date(smartMeter.since_connected), "MMM D, YYYY")
              : "",
          type: unit.pricing_model,
          type_value: unit.pricing_model_value,
          account_number: smartMeter.account_number,
        }));
      });

      setResults(smartMeters);
      setUnitNames(names);
      if (smartMeters.length) initMeters(smartMeters[0]);

      window.addEventListener("resize", getBlockWidth);
      getBlockWidth();
    });

    return () => {
      setResults([]);
      setUnitNames([]);
      setMeters([]);
    };
  }, []);

  const getBlockWidth = () => setBlockWidth(boxRef.current.clientWidth);

  const initMeters = (meters) => {
    setMeters(meters);
    setActiveAccountNumbers(meters.map((meter) => meter.account_number));
  };

  const changeTab = (i) => {
    setTab(i);
    initMeters(results[i]);
  };

  const getPricingText = (type, value) => {
    if (type === "FIX_PRICE") {
      return `${currencyFormatter(value, currencySymbol)}/Mon`;
    } else if (type === "PERCENT_SPACE") {
      return `${value} % Of Rental`;
    }

    return value;
  };

  if (!results.length) return <NoDataCard title="Units information" />;

  return (
    <Grid item className={meters?.length > 2 ? classes.scroll : classes.noScroll}>
      <Grid item className={meters?.length > 2 ? null : classes.noScrollHeight}>
        <ShaDowBox fullHeight>
          <Box ref={boxRef}>
            <Grid
              container
              style={{
                background: COLORS.darks[3],
                borderRadius: "8px 8px 0 0",
                maxWidth: blockWidth,
              }}
              wrap="nowrap"
            >
              <Grid item xs={meters?.length ? 9 : 12}>
                <TabsComponent
                  tab={tab}
                  setTab={changeTab}
                  tabNames={unitNames}
                  tabsStyle={{
                    backgroundColor: COLORS.darks[3],
                    borderRadius: "8px 8px 0 0",
                  }}
                />
              </Grid>
              <Grid
                container
                item
                xs={meters?.length ? 3 : false}
                alignItems="center"
                justifyContent="center"
                style={{ borderBottom: `4px solid ${COLORS.americanSilver}` }}
              >
                {meters?.length ? (
                  <Typography>Account: {activeAccountNumbers[0]}</Typography>
                ) : null}
              </Grid>
            </Grid>
          </Box>
          {meters?.length ? (
            <Box>
              <Grid container className={classes.header}>
                <Grid item xs={3}></Grid>
                <Grid item xs={3}>
                  <Typography className={classes.headerText}>Ownership</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={classes.headerText}>Status</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography className={classes.headerText}>Pricing</Typography>
                </Grid>
              </Grid>
              <Box>
                {meters.map((item, i) => (
                  <Grid key={i} container className={classes.row}>
                    <Grid
                      item
                      xs={3}
                      className={classes.rowItem}
                      container
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography className={classes.meterTitle}>MPAN {item.mpan}</Typography>
                    </Grid>
                    <Grid item xs={3} className={classes.rowItem}>
                      {owner[item.ownership] ? (
                        <Box>
                          <Box className={classes.icon}>
                            <Icon
                              src={owner[item.ownership].iconSrc}
                              style={{
                                width: owner[item.ownership].width,
                                height: owner[item.ownership].height,
                              }}
                            />
                          </Box>
                          <Typography className={classes.boldText}>
                            {owner[item.ownership].text}
                          </Typography>
                        </Box>
                      ) : (
                        <NA />
                      )}
                    </Grid>
                    <Grid item xs={3} className={classes.rowItem}>
                      {item.status ? (
                        <Box>
                          <Box className={classes.icon}>
                            <Icon
                              src={iconSrc[item.status]}
                              style={{
                                width: 50,
                                height: 50,
                              }}
                            />
                          </Box>
                          <Typography className={classes.boldText}>
                            {title[item.status] || "Connected"}
                          </Typography>
                          <Typography style={{ textAlign: "center" }}>
                            {item.since_connected}
                          </Typography>
                        </Box>
                      ) : (
                        <NA />
                      )}
                    </Grid>
                    <Grid item xs={3} className={classes.rowItem}>
                      {pricingType[item.type] ? (
                        <Box>
                          <Box className={classes.icon}>
                            {pricingType[item.type].component || (
                              <Icon
                                src={pricingType[item.type].icon}
                                style={{
                                  height: pricingType[item.type].height,
                                  width: pricingType[item.type].width,
                                }}
                              />
                            )}
                          </Box>
                          <Typography className={classes.boldText}>
                            {getPricingText(item.type, 0)}
                          </Typography>
                        </Box>
                      ) : (
                        <NA />
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          ) : (
            <NoDataCard withShaDowBox={false} boxStyle={{ padding: 0 }} />
          )}
        </ShaDowBox>
      </Grid>
    </Grid>
  );
};
